import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  FaLeaf,
  FaFlask,
  FaBoxOpen,
  FaUsers,
  FaAward,
  FaSolarPanel,
  FaMicrochip,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

const ProcessesFacilities = () => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);

  const capabilities = [
    {
      icon: <FaLeaf className="text-4xl md:text-5xl" />,
      title: t('processesFacilities.capabilities.dehydratedFruits.title'),
      description: t('processesFacilities.capabilities.dehydratedFruits.description'),
      image: "/images/capabilities/dehydrated-fruits.webp",
    },
    {
      icon: <FaFlask className="text-4xl md:text-5xl" />,
      title: t('processesFacilities.capabilities.canningBottling.title'),
      description: t('processesFacilities.capabilities.canningBottling.description'),
      image: "/images/capabilities/canning-bottling.webp",
    },
    {
      icon: <FaBoxOpen className="text-4xl md:text-5xl" />,
      title: t('processesFacilities.capabilities.packagingExport.title'),
      description: t('processesFacilities.capabilities.packagingExport.description'),
      image: "/images/capabilities/packaging.webp",
    },
    {
      icon: <FaUsers className="text-4xl md:text-5xl" />,
      title: t('processesFacilities.capabilities.workforce.title'),
      description: t('processesFacilities.capabilities.workforce.description'),
      image: "/images/capabilities/experience.webp",
    },
    {
      icon: <FaAward className="text-4xl md:text-5xl" />,
      title: t('processesFacilities.capabilities.qualityLab.title'),
      description: t('processesFacilities.capabilities.qualityLab.description'),
      image: "/images/capabilities/lab-quality.webp",
    },
    {
      icon: <FaSolarPanel className="text-4xl md:text-5xl" />,
      title: t('processesFacilities.capabilities.solar.title'),
      description: t('processesFacilities.capabilities.solar.description'),
      image: "/images/capabilities/solar.webp",
    },
    {
      icon: <FaMicrochip className="text-4xl md:text-5xl" />,
      title: t('processesFacilities.capabilities.technology.title'),
      description: t('processesFacilities.capabilities.technology.description'),
      image: "/images/capabilities/technology.webp",
    },
    {
      icon: <FaFlask className="text-4xl md:text-5xl" />,
      title: t('processesFacilities.capabilities.rdInnovation.title'),
      description: t('processesFacilities.capabilities.rdInnovation.description'),
      image: "/images/capabilities/research.webp",
    },
  ];

  // Auto-play slider
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % capabilities.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [capabilities.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % capabilities.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + capabilities.length) % capabilities.length);
  };

  const floatingElements = [
    { emoji: "🍍", delay: 0, duration: 6, x: "5%", y: "10%" },
    { emoji: "🥥", delay: 1, duration: 7, x: "90%", y: "15%" },
    { emoji: "🥭", delay: 2, duration: 8, x: "10%", y: "80%" },
    { emoji: "🍌", delay: 1.5, duration: 6.5, x: "85%", y: "75%" },
  ];

  return (
    <section className="relative min-h-screen flex items-center bg-gradient-to-br from-green-50 via-emerald-50 to-green-100 dark:from-dark-900 dark:via-dark-800 dark:to-dark-900 overflow-hidden py-16 md:py-0">
      {/* Floating Elements - Hidden on mobile */}
      <div className="hidden md:block">
        {floatingElements.map((element, index) => (
          <motion.div
            key={index}
            className="absolute text-5xl pointer-events-none opacity-10 dark:opacity-5"
            style={{ left: element.x, top: element.y }}
            animate={{
              y: [0, -20, 0],
              rotate: [0, 10, -10, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              repeat: Infinity,
              duration: element.duration,
              delay: element.delay,
              ease: "easeInOut",
            }}
          >
            {element.emoji}
          </motion.div>
        ))}
      </div>

      <div className="container mx-auto px-4 md:px-0 relative z-10">
        {/* Mobile: Stacked Layout | Desktop: Side-by-side */}
        <div className="flex flex-col lg:grid lg:grid-cols-2 lg:min-h-screen gap-8 lg:gap-0">
          
          {/* Text Content Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex items-center px-4 md:px-8 lg:px-16 xl:px-20 py-8 lg:py-20"
          >
            <div className="w-full">
              <h2 className="text-3xl md:text-4xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                {t('processesFacilities.title')}
              </h2>

              <div className="space-y-4 text-sm md:text-base  text-gray-700 dark:text-gray-300 leading-relaxed">
                <p>
                  {t('processesFacilities.description.paragraph1.part1')}{" "}
                  <strong className="text-primary-600 dark:text-primary-400">
                    {t('processesFacilities.description.paragraph1.highlight1')}
                  </strong>{" "}
                  {t('processesFacilities.description.paragraph1.part2')}
                </p>

                <p>
                  {t('processesFacilities.description.paragraph2.part1')}{" "}
                  <strong className="text-primary-600 dark:text-primary-400">
                    {t('processesFacilities.description.paragraph2.highlight1')}
                  </strong>
                  , {t('processesFacilities.description.paragraph2.part2')}{" "}
                  <span className="text-primary-600 dark:text-primary-400 font-semibold">
                    {t('processesFacilities.description.paragraph2.highlight2')}
                  </span>
                  , {t('processesFacilities.description.paragraph2.part3')}
                </p>

                <p>
                  {t('processesFacilities.description.paragraph3.part1')}{" "}
                  <strong className="text-primary-700 dark:text-primary-300">
                    {t('processesFacilities.description.paragraph3.highlight1')}
                  </strong>{" "}
                  {t('processesFacilities.description.paragraph3.part2')}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Slider Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative flex items-center px-4 md:px-0"
          >
            {/* Slider Container */}
            <div className="relative w-full h-[500px] md:h-[600px] lg:h-full rounded-2xl lg:rounded-none overflow-hidden shadow-2xl lg:shadow-none group">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, scale: 1.05 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                  className="absolute inset-0"
                >
                  {/* Background Image */}
                  <div className="absolute inset-0">
                    <img
                      src={capabilities[currentSlide].image}
                      alt={capabilities[currentSlide].title}
                      className="w-full h-full object-cover"
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                  </div>

                  {/* Content Card */}
                  <div className="absolute inset-0 flex items-end justify-center p-6 md:p-8 lg:p-12">
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3, duration: 0.5 }}
                      className="bg-white/95 dark:bg-dark-800/95 backdrop-blur-md rounded-2xl shadow-2xl p-6 md:p-8 w-full max-w-lg"
                    >
                      {/* Icon */}
                      <div className="mb-4 text-primary-600 dark:text-primary-400">
                        {capabilities[currentSlide].icon}
                      </div>

                      {/* Title */}
                      <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-3">
                        {capabilities[currentSlide].title}
                      </h3>

                      {/* Description */}
                      <p className="text-sm md:text-base text-gray-700 dark:text-gray-300 leading-relaxed line-clamp-4">
                        {capabilities[currentSlide].description}
                      </p>
                    </motion.div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-dark-800/90 hover:bg-white dark:hover:bg-dark-700 text-gray-800 dark:text-white p-3 md:p-4 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 z-20"
                aria-label="Previous slide"
              >
                <FaChevronLeft className="text-lg md:text-xl" />
              </button>

              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-dark-800/90 hover:bg-white dark:hover:bg-dark-700 text-gray-800 dark:text-white p-3 md:p-4 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 z-20"
                aria-label="Next slide"
              >
                <FaChevronRight className="text-lg md:text-xl" />
              </button>

              {/* Slide Counter */}
              <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs md:text-sm font-semibold z-20">
                {currentSlide + 1} / {capabilities.length}
              </div>

              {/* Progress Dots */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                {capabilities.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`transition-all duration-300 rounded-full ${
                      currentSlide === index
                        ? "w-8 md:w-10 h-2 bg-white"
                        : "w-2 h-2 bg-white/50 hover:bg-white/75"
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProcessesFacilities;
